import React from 'react';
import {
  FaChartLine,
  FaClipboardCheck,
  FaHeadset,
  FaMoon,
  FaSun,
} from 'react-icons/fa';
import { useTheme } from '../common/theme';

/*
 * Shared shell for every screen that renders before the app shell exists.
 *
 * Split layout: a brand panel that says what the product is, and a card that
 * holds whatever the current auth state needs. The panel collapses on small
 * screens so the card is never pushed below the fold.
 *
 * Theme-aware: these screens render outside <App/>, so the useTheme call
 * here is what applies the stored (or system) preference before sign-in —
 * without it a returning dark-mode user gets flashed with a light login.
 * The form side follows the theme via the global `.dark` remap rules in
 * index.css (its `bg-white` becomes the dark card surface); the brand panel
 * deliberately CONTRASTS with it — light lavender when the form side is
 * dark, dark ink when the form side is light — so the split layout always
 * reads as two distinct halves and toggling gives immediate feedback.
 * The corner toggle persists through the same hook the app shell uses, so
 * the choice carries into the app.
 */

const HIGHLIGHTS = [
  { icon: <FaHeadset />, text: 'Every call transcribed and searchable' },
  { icon: <FaClipboardCheck />, text: 'Scorecards applied automatically' },
  { icon: <FaChartLine />, text: 'Quality and compliance trends at a glance' },
];

// Shared decorative glows. The light wash also covers the form side when that
// side is white, so the whole screen reads as one surface in light mode.
const LIGHT_GLOW =
  'radial-gradient(60rem 40rem at 15% 10%, rgba(124,58,237,0.14), transparent 60%),' +
  'radial-gradient(45rem 35rem at 90% 85%, rgba(20,184,166,0.10), transparent 60%)';
const DARK_GLOW =
  'radial-gradient(60rem 40rem at 15% 10%, rgba(124,58,237,0.45), transparent 60%),' +
  'radial-gradient(45rem 35rem at 90% 85%, rgba(20,184,166,0.28), transparent 60%)';

// `glow` covers the white form side with the brand gradient in light mode.
// The sign-in screen wants it; the post-login AuthLoading screen doesn't —
// it should read as the plain app surface being prepared, not more branding.
const AuthLayout = ({ children, glow = true }) => {
  const { theme, toggleTheme } = useTheme();
  const dark = theme === 'dark';
  // The black-text logo disappears on the dark card, so the mobile header
  // swaps to the light logo the brand panel already uses.
  const logo = `${process.env.PUBLIC_URL}/images/${
    dark ? 'cloudza-logo.png' : 'cloudza-logo-black.png'
  }`;

  return (
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-2">
      {/* Brand panel — decorative, so it is skipped on small screens entirely
          rather than being stacked and scrolled past. */}
      <div
        className={`relative hidden overflow-hidden p-12 lg:flex lg:flex-col lg:justify-between transition-colors ${
          dark ? 'bg-[#f3f1fa]' : 'bg-ink'
        }`}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{ background: dark ? LIGHT_GLOW : DARK_GLOW }}
        />

        <img
          src={`${process.env.PUBLIC_URL}/images/cloudza-logo${dark ? '-black' : ''}.png`}
          alt="CloudZA"
          className="relative h-8 w-auto object-contain object-left"
        />

        <div className="relative">
          {/* Inline color: the global `.dark h2` rule outranks any single
              text utility, and this heading must stay dark on the light
              panel that dark mode shows. */}
          <h2
            className="max-w-md text-4xl font-semibold leading-tight"
            style={{ color: dark ? '#17151f' : '#ffffff' }}
          >
            Know what happens on every call.
          </h2>
          <p
            className={`mt-4 max-w-md text-base leading-relaxed ${
              dark ? 'text-[#5b5670]' : 'text-white/70'
            }`}
          >
            Post-Call Analytics turns your contact centre recordings into scored,
            searchable, reviewable records.
          </p>

          <ul className="mt-10 space-y-4">
            {HIGHLIGHTS.map((h) => (
              <li
                key={h.text}
                className={`flex items-center gap-3 text-sm ${
                  dark ? 'text-[#413d52]' : 'text-white/80'
                }`}
              >
                <span
                  className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                    dark
                      ? 'bg-cloudza-600/10 text-[#6d28d9]'
                      : 'bg-white/10 text-white'
                  }`}
                >
                  {h.icon}
                </span>
                {h.text}
              </li>
            ))}
          </ul>
        </div>

        <p className={`relative text-xs ${dark ? 'text-[#a3a0b0]' : 'text-white/40'}`}>
          &copy; {new Date().getFullYear()} CloudZA. All rights reserved.
        </p>
      </div>

      {/* Auth panel */}
      <div className="relative flex min-h-screen items-center justify-center p-6 sm:p-10">
        {/* In light mode the form side is white, so it carries the same soft
            glow as the brand panel — one continuous surface across the split.
            (The wash div is positioned, so in-flow siblings need `relative`
            to paint above it.) */}
        {glow && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-70"
            style={{ background: dark ? DARK_GLOW : LIGHT_GLOW }}
          />
        )}
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          className="absolute right-4 top-4 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
        >
          {dark ? <FaSun /> : <FaMoon />}
        </button>
        <div className="relative w-full max-w-sm">
          <img
            src={logo}
            alt="CloudZA"
            className="mb-8 h-8 w-auto object-contain lg:hidden"
          />
          {children}
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
